import { useEffect, useState } from "react";
import { PageHeader } from "../../components";
import BasicDetails from "./BasicDetails";
import SalaryDetails from "./SalaryDetails";
import StepsBar from "../../components/StepsBar";
import PaymentInformation from "./PaymentInformation";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import StaffApis from "../../api/staffApis";
import { Staff } from "../../types/staff";

const sections = [
  { id: 1, name: "Basic Details" },
  { id: 2, name: "Salary Details" },
  { id: 3, name: "Payment Information" },
];

export default function AddPayroll() {
  const { id } = useParams(); // staff id
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [staffData, setStaffData] = useState<Staff | null>(null);

  useEffect(() => {
    const fetchStaff = async () => {
      if (!id) return;

      const res = await StaffApis.getStaffById(Number(id));
      if (res?.data) {
        setStaffData(res.data);
      }
    };

    fetchStaff();
  }, [id]);

  const storageKey = `payroll-step-${id}`;

  const stepFromUrl = Number(searchParams.get("step"));
  const stepFromStorage = Number(sessionStorage.getItem(storageKey));

  const [step, setStep] = useState(stepFromUrl || stepFromStorage || 1);

  // sync step → URL (no history pollution)
  useEffect(() => {
    setSearchParams({ step: String(step) }, { replace: true });
    sessionStorage.setItem(storageKey, String(step));
  }, [step, setSearchParams, storageKey]);

  const progressKey = `payroll-progress-${id}`;
  const progress = JSON.parse(sessionStorage.getItem(progressKey) || "{}");

  useEffect(() => {
    if (!staffData) return;

    if (progress?.completed && progress?.lastPage === "overview") {
      navigate(`/payroll/staff-overview/${id}`, {
        replace: true,
      });
    }
  }, [staffData, progress, navigate]);

  useEffect(() => {
    const handleBack = () => {
      if (location.state?.fromPayrollList) {
        navigate("/payroll-management", { replace: true });
      } else {
        navigate("/payroll-management", { replace: true });
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [navigate, location.state]);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader showBack />

      <div className="flex justify-center items-center flex-col gap-5">
        <div className="flex items-center w-full max-w-[730px] mx-auto gap-6">
          <StepsBar
            current={step}
            sections={sections}
            onStepClick={(clickedStep) => {
              if (clickedStep <= step) setStep(clickedStep);
            }}
          />
        </div>
        <div className="w-full max-w-4xl flex flex-col">
          {step === 1 && (
            <BasicDetails data={staffData} onNext={() => setStep(2)} />
          )}
          {step === 2 && (
            <SalaryDetails
              data={staffData}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && <PaymentInformation onBack={() => setStep(2)} />}
        </div>
      </div>
    </div>
  );
}

export function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-neutral-300 text-[13px]">{label}</span>
      <span className="text-neutral-500 text-sm font-medium">{value}</span>
    </div>
  );
}
